class CustomCarousel extends HTMLElement {
  constructor() {
    super();
    this.wasDragged = false;
    this.dragClickThreshold = 5; // pixels — small movement ignored as a click
  }

  connectedCallback() {
    this.slides = this.querySelectorAll('.custom-carousel__slide');
    this.prevButton = this.querySelector('.custom-carousel__arrow--prev');
    this.nextButton = this.querySelector('.custom-carousel__arrow--next');
    this.slideContainer = this.querySelector('.custom-carousel__main');
    this.track = this.querySelector('.custom-carousel__track');
    this.visibleSlides = [];

    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.initialOffset = 0;

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;


    document.addEventListener('alpine:init', async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      this.stateUpdated();
      if (this.totalSlides > 0) {
        this.setupCarousel();
      }
    });

    document.addEventListener('userSelectedLocationChanged', () => this.stateUpdated());
  }

  disconnectedCallback() {
    this.prevButton.removeEventListener('click', () => this.showPrevSlide());
    this.nextButton.removeEventListener('click', () => this.showNextSlide());

    document.removeEventListener('userSelectedLocationChanged', () => this.stateUpdated());
    document.removeEventListener('alpine:init', () => this.stateUpdated());
  }

  stateUpdated() {
    let totalVisibleSlides = 0;
    this.visibleSlides = [];
    this.slides.forEach(slide => {
      const productStates = JSON.parse(slide.dataset.productStates);
      const isVisible = Alpine.store('userVerificationData').checkVisibility(productStates);

      if (isVisible) {
        totalVisibleSlides++;
        this.visibleSlides.push(slide);
      }
    });
    this.totalSlides = totalVisibleSlides;
  }

  setupCarousel() {
    this.slideContainer.style.display = 'flex';
    this.updateSlides();

    this.prevButton.addEventListener('click', () => this.showPrevSlide());
    this.nextButton.addEventListener('click', () => this.showNextSlide());

    // Add touch events for dragging
    this.slideContainer.addEventListener('touchstart', (e) => this.dragStart(e), { passive: false });
    this.slideContainer.addEventListener('touchmove', (e) => this.dragging(e), { passive: false });
    this.slideContainer.addEventListener('touchend', () => this.dragEnd());

    // Add mouse events for dragging
    this.slideContainer.addEventListener('mousedown', (e) => this.dragStart(e));
    this.slideContainer.addEventListener('mousemove', (e) => this.dragging(e));
    this.slideContainer.addEventListener('mouseup', () => this.dragEnd());
    this.slideContainer.addEventListener('mouseleave', () => this.dragEnd());

    // Recalculate on resize
    new ResizeObserver(() => this.updateSlides()).observe(this.track);
  }

  showPrevSlide() {
    this.currentIndex = Math.max(this.currentIndex - 1, 0);
    this.updateSlides();
  }

  showNextSlide() {
    this.currentIndex = Math.min(this.currentIndex + 1, this.totalSlides - this.totalVisibleSlides);
    this.updateSlides();
  }

  dragStart(e) {
    this.isDragging = true;
    this.slideContainer.style.transition = 'none'; // Disable transition during drag
    this.startX = e.pageX || (e.touches && e.touches[0] && e.touches[0].pageX) || 0;
    // initialize currentX to startX so a simple click (no move) doesn't create a large diff
    this.currentX = this.startX;
    this.wasDragged = false;

    const transformMatrix = window.getComputedStyle(this.slideContainer).getPropertyValue('transform');
    if (transformMatrix !== 'none') {
      this.initialOffset = parseInt(transformMatrix.split(',')[4], 10);
    } else {
      this.initialOffset = 0;
    }
  }

  dragging(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    this.currentX = e.pageX || (e.touches && e.touches[0] && e.touches[0].pageX) || this.currentX;
    const diff = this.currentX - this.startX;
    if (!this.wasDragged && Math.abs(diff) > this.dragClickThreshold) this.wasDragged = true;
    this.slideContainer.style.transform = `translateX(${this.initialOffset + diff}px)`;
  }

  dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.slideContainer.style.transition = ''; // Re-enable transition

    const diff = this.currentX - this.startX;
    // If user didn't actually drag (only clicked), just snap back and do nothing
    if (!this.wasDragged) {
      this.updateSlides();
      return;
    }

    // Otherwise treat it as a swipe only if it's a significant movement
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.showPrevSlide();
      } else {
        this.showNextSlide();
      }
    } else {
      this.updateSlides(); // Snap back to original position
    }
  }

  updateSlides() {
    const slides = this.visibleSlides.length > 0 ? this.visibleSlides : this.slides;
    this.totalVisibleSlides = Math.floor(this.track.offsetWidth / (slides[0].offsetWidth + 40)); // 40 is for margin-right: 2.5rem

    let offset = 0;
    if (this.currentIndex > 0) {
      const slideWidth = slides[0].offsetWidth + 40; // 40 is for margin-right: 2.5rem
      offset = -this.currentIndex * slideWidth;
    }

    this.slideContainer.style.transform = `translateX(${offset}px)`;

    // Update button states
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex >= this.totalSlides - this.totalVisibleSlides;

    // Hide arrows when every product already fits (nothing to scroll)
    const noOverflow = this.totalSlides <= this.totalVisibleSlides;
    this.prevButton.hidden = noOverflow;
    this.nextButton.hidden = noOverflow;

    // A11y
    slides.forEach((slide, index) => {
      const isVisible = index >= this.currentIndex && index < this.currentIndex + this.totalVisibleSlides;
      if (isVisible) {
        slide.removeAttribute('aria-hidden');
        slide.setAttribute('tabindex', '0');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
    });
  }
}

if (!customElements.get('custom-carousel')) {
  customElements.define('custom-carousel', CustomCarousel);
}
